import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { RelatoriosApi } from './relatorios.api.js';
import { logger } from '../../core/logger.js';

/**
 * Card "Vendas por origem" — de onde vieram as vendas PAGAS do site (anúncio Meta, Google Ads,
 * Google orgânico, Instagram, acesso direto...). Dados de `GET /relatorios/vendas-por-origem`
 * (origem capturada no site, `orders.attribution`). Reaproveita o CSS do card "Site e tráfego"
 * (`CSS_SITE_TRAFEGO`, classes `site-*`), então a página só precisa injetar aquele CSS uma vez.
 */

export const HTML_CARD_VENDAS_ORIGEM = `
  <pg-card class="secao site-trafego-card">
    <span slot="header">Vendas por origem</span>
    <div slot="header-acao" class="site-dias">
      <button type="button" data-vo-dias="30" class="site-dias-btn vo-dias-btn is-ativo">30 dias</button>
      <button type="button" data-vo-dias="90" class="site-dias-btn vo-dias-btn">90 dias</button>
    </div>
    <div id="vendas-origem-body"><pg-empty-state variant="carregando" titulo="Vendas por origem"></pg-empty-state></div>
  </pg-card>
`;

function esc(valor: string): string {
  return valor.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export function ligarCardVendasOrigem(root: ShadowRoot | HTMLElement, api: RelatoriosApi | null): void {
  let dias: 30 | 90 = 30;
  root.querySelectorAll<HTMLButtonElement>('.vo-dias-btn').forEach((botao) => {
    botao.addEventListener('click', () => {
      const novo = botao.dataset['voDias'] === '90' ? 90 : 30;
      if (novo === dias) return;
      dias = novo;
      root.querySelectorAll('.vo-dias-btn').forEach((b) => b.classList.toggle('is-ativo', b === botao));
      void carregar(root, api, dias);
    });
  });
  void carregar(root, api, dias);
}

async function carregar(root: ShadowRoot | HTMLElement, api: RelatoriosApi | null, dias: 30 | 90): Promise<void> {
  const body = root.querySelector('#vendas-origem-body');
  if (!api || !body) return;
  body.innerHTML = '<pg-empty-state variant="carregando" titulo="Vendas por origem"></pg-empty-state>';
  try {
    const resultado = await api.obterVendasPorOrigem(dias);
    if (resultado.status === 'sem-permissao') {
      body.innerHTML = '<pg-empty-state variant="sem-permissao" titulo="Vendas por origem"></pg-empty-state>';
      return;
    }
    if (resultado.status !== 'ok') {
      const msg = resultado.status === 'erro' ? resultado.mensagem : 'indisponível';
      body.innerHTML = `<pg-empty-state variant="erro" titulo="Vendas por origem" descricao="${esc(msg)}"></pg-empty-state>`;
      return;
    }
    const v = resultado.dado;
    if (!v.disponivel) {
      body.innerHTML = `<pg-empty-state variant="indisponivel" titulo="Vendas por origem" descricao="${esc(v.motivo ?? 'indisponível')}"></pg-empty-state>`;
      return;
    }
    if (!v.totalPedidos) {
      body.innerHTML = `<pg-empty-state variant="vazio" titulo="Vendas por origem" descricao="nenhuma venda paga pelo site nos últimos ${dias} dias"></pg-empty-state>`;
      return;
    }
    const brl = (c: number): string => formatBRL(centavosParaReais(c));
    const max = Math.max(1, ...(v.canais ?? []).map((c) => c.valorCentavos));
    const linhas = (v.canais ?? [])
      .map(
        (c) => `<div class="site-linha"><span>${esc(c.canal)} <small style="color:var(--pg-color-texto-secundario,#9aa1ab)">(${c.pedidos} ${c.pedidos === 1 ? 'pedido' : 'pedidos'})</small></span><b>${esc(brl(c.valorCentavos))}</b>
          <span class="site-barra"><i style="width:${Math.round((c.valorCentavos / max) * 100)}%"></i></span></div>`
      )
      .join('');
    const campanhas = (v.campanhas ?? []).length
      ? (v.campanhas ?? [])
          .map((c) => `<div class="site-linha"><span>${esc(c.campanha)} <small style="color:var(--pg-color-texto-secundario,#9aa1ab)">(${c.pedidos})</small></span><b>${esc(brl(c.valorCentavos))}</b></div>`)
          .join('')
      : '<div class="site-nota">nenhuma venda com link de campanha (UTM) no período</div>';
    body.innerHTML = `
      <div class="site-kpis">
        <div class="site-kpi"><small>Vendas pagas pelo site</small><b>${v.totalPedidos}</b></div>
        <div class="site-kpi"><small>Faturado</small><b>${esc(brl(v.totalCentavos ?? 0))}</b></div>
      </div>
      <div class="site-grid">
        <div class="site-bloco"><h4>Por canal</h4>${linhas}</div>
        <div class="site-bloco"><h4>Por campanha (UTM)</h4>${campanhas}</div>
      </div>
      <div class="site-nota">Últimos ${v.dias} dias, só vendas pagas feitas pelo site. A origem começou a ser registrada em 03/11/2026: vendas anteriores aparecem como "Sem origem registrada". Atualiza a cada 15 minutos.</div>`;
  } catch (erro) {
    logger.erro('falha ao carregar vendas por origem', { erro });
    body.innerHTML = '<pg-empty-state variant="erro" titulo="Vendas por origem" descricao="falha ao carregar"></pg-empty-state>';
  }
}
